import { Suspense, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, Center, Environment } from '@react-three/drei';
import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import PageHeader from '../components/common/PageHeader';
import FABRIC_LIBRARY, { MATERIAL_PROPERTIES } from '../data/fabricLibrary';
import FINISH_LIBRARY from '../data/finishLibrary';

// ============================================================================
// CHANNELS - the set of things a material part can be mapped to
// ============================================================================
const CHANNELS = [
  { id: 'metal', label: 'Metal', color: 'warning' },
  { id: 'wood', label: 'Wood', color: 'secondary' },
  { id: 'cane', label: 'Cane', color: 'primary' },
  { id: 'fabric1', label: 'Fabric 1', color: 'success' },
  { id: 'fabric2', label: 'Fabric 2', color: 'success' },
  { id: 'fabric3', label: 'Fabric 3', color: 'success' },
  { id: 'ignore', label: 'Keep original', color: 'default' },
];

const FABRIC_CHANNEL_IDS = ['fabric1', 'fabric2', 'fabric3'];

// ============================================================================
// PRESETS for wood and cane (these stay procedural for now)
// ============================================================================
const WOOD_PRESETS = {
  walnut: { label: 'Walnut', color: '#5c3a21', roughness: 0.55 },
  natural_oak: { label: 'Natural Oak', color: '#c19a6b', roughness: 0.7 },
  white_oak: { label: 'White Oak', color: '#d4b896', roughness: 0.75 },
  ebony: { label: 'Ebony', color: '#1a1410', roughness: 0.4 },
  cherry: { label: 'Cherry', color: '#7a3b29', roughness: 0.5 },
  teak: { label: 'Teak', color: '#996b3d', roughness: 0.6 },
  whitewash: { label: 'Whitewash', color: '#e8dfd1', roughness: 0.7 },
};

const CANE_PRESETS = {
  natural: { label: 'Natural Cane', color: '#e6d4a8' },
  white: { label: 'White Wash', color: '#f0eadc' },
  honey: { label: 'Honey', color: '#d4a76a' },
  dark: { label: 'Dark Walnut', color: '#6b4a2b' },
  black: { label: 'Black', color: '#2a2520' },
};

// ============================================================================
// AUTO-CLASSIFY - only used as a *first guess* for the mapping; user overrides freely
// ============================================================================
const METAL_PATTERNS = ['bacak', 'altin', 'metal', 'steel', 'chrome', 'brass', 'frame', 'leg'];
const FABRIC_PATTERNS = ['kumas', 'fabric', 'cloth', 'seat', 'cushion', 'upholster', 'textile'];
const WOOD_PATTERNS = ['weneer', 'veneer', 'wood', 'ahsap', 'oak', 'walnut'];
const CANE_PATTERNS = ['cane', 'rattan', 'wicker', 'mesh'];

const autoClassifyChannel = (mat) => {
  const name = (mat.name || '').toLowerCase();
  if (METAL_PATTERNS.some((p) => name.includes(p))) return 'metal';
  if (CANE_PATTERNS.some((p) => name.includes(p))) return 'cane';
  if (WOOD_PATTERNS.some((p) => name.includes(p))) return 'wood';
  if (FABRIC_PATTERNS.some((p) => name.includes(p))) return 'fabric1';
  if (mat.metalness !== undefined && mat.metalness > 0.4) return 'metal';
  return 'ignore';
};

// ============================================================================
// PERSISTENT MAPPING STORE - keyed by material name (lowercase, trimmed)
// So once "KumasMaterial" is mapped to fabric1 on one model, future models
// with that same material name auto-inherit the mapping.
// ============================================================================
const MAPPINGS_KEY = 'configurator.materialMappings.v1';

const loadStoredMappings = () => {
  try {
    return JSON.parse(localStorage.getItem(MAPPINGS_KEY) || '{}');
  } catch {
    return {};
  }
};

const saveStoredMappings = (mappings) => {
  try {
    localStorage.setItem(MAPPINGS_KEY, JSON.stringify(mappings));
  } catch {
    // localStorage full or disabled — fail silent
  }
};

const matKey = (name) => (name || 'unnamed').toLowerCase().trim();

// ============================================================================
// File format detection
// ============================================================================
const detectModelFormat = (value) => {
  const normalized = (value || '').split('?')[0].split('#')[0].toLowerCase();
  if (normalized.endsWith('.fbx')) return 'fbx';
  if (normalized.endsWith('.glb')) return 'glb';
  if (normalized.endsWith('.gltf')) return 'gltf';
  if (normalized.endsWith('.obj')) return 'obj';
  return '';
};

// ============================================================================
// Procedural textures for wood and cane
// ============================================================================
const generateWoodTexture = (hexColor, size = 256) => {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = hexColor;
  ctx.fillRect(0, 0, size, size);
  for (let i = 0; i < 40; i++) {
    const y = Math.random() * size;
    const opacity = 0.08 + Math.random() * 0.18;
    ctx.strokeStyle = Math.random() > 0.4
      ? `rgba(0,0,0,${opacity})`
      : `rgba(255,255,255,${opacity * 0.5})`;
    ctx.lineWidth = 0.5 + Math.random() * 1.5;
    ctx.beginPath();
    ctx.moveTo(0, y);
    for (let x = 0; x <= size; x += 8) {
      ctx.lineTo(x, y + Math.sin(x * 0.04) * 2 + (Math.random() - 0.5) * 1.5);
    }
    ctx.stroke();
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(2, 2);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
};

const generateCaneTexture = (hexColor, size = 256) => {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = hexColor;
  ctx.fillRect(0, 0, size, size);
  const spacing = 16;
  ctx.strokeStyle = 'rgba(0,0,0,0.55)';
  ctx.lineWidth = 1.2;
  for (let row = 0; row * spacing < size; row++) {
    for (let col = 0; col * spacing < size; col++) {
      const x = col * spacing + (row % 2 ? spacing / 2 : 0);
      const y = row * spacing;
      ctx.beginPath();
      ctx.arc(x, y, spacing * 0.35, 0, Math.PI * 2);
      ctx.stroke();
    }
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(3, 3);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
};

// ============================================================================
// Model loader
// ============================================================================
const loadModelFromUrl = (url, format) => {
  return new Promise((resolve, reject) => {
    let loader;
    if (format === 'fbx') loader = new FBXLoader();
    else if (format === 'glb' || format === 'gltf') loader = new GLTFLoader();
    else if (format === 'obj') loader = new OBJLoader();
    else return reject(new Error('Unsupported format'));

    loader.load(
      url,
      (result) => {
        const scene = result.scene || result;

        // Auto-orient Z-up models
        let box = new THREE.Box3().setFromObject(scene);
        const dims = new THREE.Vector3();
        box.getSize(dims);
        if (dims.z > dims.y * 1.3 && dims.z > dims.x * 0.7) {
          scene.rotation.x = -Math.PI / 2;
          scene.updateMatrixWorld(true);
          box = new THREE.Box3().setFromObject(scene);
          box.getSize(dims);
        }

        const maxDim = Math.max(dims.x, dims.y, dims.z);
        if (maxDim > 0) scene.scale.multiplyScalar(2.5 / maxDim);

        // Collect unique materials
        const mats = [];
        const seen = new Set();
        scene.traverse((child) => {
          if (!child.isMesh) return;
          const materials = Array.isArray(child.material) ? child.material : [child.material];
          materials.forEach((mat) => {
            if (!mat || seen.has(mat.uuid)) return;
            seen.add(mat.uuid);
            mats.push({
              name: mat.name || 'unnamed',
              uuid: mat.uuid,
              autoChannel: autoClassifyChannel(mat),
            });
          });
        });

        // Spread multiple fabric materials across Fabric 1, 2, 3 so a chair with
        // separate seat/back fabrics is independently configurable out of the box.
        const fabricSlots = ['fabric1', 'fabric2', 'fabric3'];
        let fabricIdx = 0;
        mats.forEach((m) => {
          if (m.autoChannel === 'fabric1') {
            m.autoChannel = fabricSlots[Math.min(fabricIdx, fabricSlots.length - 1)];
            fabricIdx += 1;
          }
        });

        resolve({ scene, materialMap: mats });
      },
      undefined,
      (error) => reject(error)
    );
  });
};

// ============================================================================
// LoadedModel — applies the channels object to the scene based on the mapping
// ============================================================================
const LoadedModel = ({ scene, mappings, channels }) => {
  const { invalidate } = useThree();

  useEffect(() => {
    if (!scene) return;
    scene.traverse((child) => {
      if (!child.isMesh) return;
      child.castShadow = true;
      child.receiveShadow = true;

      if (!child.userData._origMats) {
        const mats = Array.isArray(child.material) ? child.material : [child.material];
        child.userData._origMats = mats.map((m) => ({
          material: m.clone(),
          key: matKey(m.name),
        }));
      }

      const origEntries = child.userData._origMats;
      const newMats = origEntries.map((entry) => {
        const channel = mappings[entry.key] || 'ignore';
        if (channel === 'ignore') return entry.material;
        return channels[channel] || entry.material;
      });

      child.material = newMats.length === 1 ? newMats[0] : newMats;
    });
    invalidate();
  }, [scene, mappings, channels, invalidate]);

  if (!scene) return null;
  return (
    <Center>
      <primitive object={scene} />
    </Center>
  );
};

const FallbackChair = ({ metalPreset }) => (
  <group position={[0, -0.8, 0]}>
    <mesh position={[0, 0.8, 0]} castShadow receiveShadow>
      <boxGeometry args={[1.5, 0.22, 1.5]} />
      <meshStandardMaterial color="#6b2142" roughness={0.7} />
    </mesh>
    <mesh position={[0, 1.4, -0.6]} castShadow receiveShadow>
      <boxGeometry args={[1.5, 1.1, 0.2]} />
      <meshStandardMaterial color="#6b2142" roughness={0.7} />
    </mesh>
    {[
      [-0.62, 0.25, -0.62],
      [0.62, 0.25, -0.62],
      [-0.62, 0.25, 0.62],
      [0.62, 0.25, 0.62],
    ].map((position, idx) => (
      <mesh key={idx} position={position} castShadow receiveShadow>
        <cylinderGeometry args={[0.06, 0.06, 0.7, 20]} />
        <meshStandardMaterial
          color={metalPreset.tint || '#ffffff'}
          metalness={metalPreset.metalness}
          roughness={metalPreset.roughness}
        />
      </mesh>
    ))}
  </group>
);

// ============================================================================
// Swatch components
// ============================================================================
const FabricSwatch = ({ url, name, selected, onClick }) => (
  <Tooltip title={name} arrow>
    <IconButton
      onClick={onClick}
      sx={{
        width: 44,
        height: 44,
        borderRadius: 1.5,
        border: selected ? '3px solid #6c63ff' : '2px solid #ccc',
        boxShadow: selected ? '0 0 0 2px #6c63ff40' : 'none',
        p: 0,
        overflow: 'hidden',
        backgroundImage: `url("${encodeURI(url)}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    />
  </Tooltip>
);

const ColorSwatch = ({ color, selected, onClick, label }) => (
  <Tooltip title={label} arrow>
    <IconButton
      onClick={onClick}
      sx={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        bgcolor: color,
        border: selected ? '3px solid #6c63ff' : '2px solid #ccc',
        boxShadow: selected ? '0 0 0 2px #6c63ff40' : 'none',
        '&:hover': { bgcolor: color, opacity: 0.85 },
        p: 0,
      }}
    />
  </Tooltip>
);

// ============================================================================
// FabricPickerCard — reusable card for Fabric 1, 2, 3
// ============================================================================
const FabricPickerCard = ({
  channelId,
  label,
  partsCount,
  state,
  setState,
  linkOptions = [],
  linkedTo = null,
  setLinkedTo,
}) => {
  const fabricTypes = Object.keys(FABRIC_LIBRARY);
  const collectionsForType = Object.keys(FABRIC_LIBRARY[state.type] || {});
  const swatchesForCollection = FABRIC_LIBRARY[state.type]?.[state.collection] || [];
  const currentSwatch = swatchesForCollection[state.swatchIdx] || swatchesForCollection[0];
  // When linked to another fabric, this card mirrors that fabric's selection
  // and its own controls are locked.
  const linked = !!linkedTo;

  const updateType = (newType) => {
    const cols = Object.keys(FABRIC_LIBRARY[newType] || {});
    setState({ type: newType, collection: cols[0] || '', swatchIdx: 0 });
  };

  const updateCollection = (newCollection) => {
    setState({ ...state, collection: newCollection, swatchIdx: 0 });
  };

  return (
    <Card sx={{ p: 2.5 }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {label}
          </Typography>
          <Chip
            size="small"
            label={`${partsCount} part${partsCount > 1 ? 's' : ''}`}
            color="info"
            variant="outlined"
          />
        </Stack>

        {/* "Same as Fabric N" — mirror another fabric so the user doesn't have
            to re-pick the same selection. Mutually exclusive; unchecking
            returns this fabric to its own independent selection. */}
        {linkOptions.length > 0 && (
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {linkOptions.map((opt) => (
              <FormControlLabel
                key={opt.id}
                sx={{ mr: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={linkedTo === opt.id}
                    onChange={(e) => setLinkedTo(e.target.checked ? opt.id : null)}
                  />
                }
                label={<Typography variant="body2">Same as {opt.label}</Typography>}
              />
            ))}
          </Stack>
        )}

        <TextField
          select
          size="small"
          label="Material Type"
          value={state.type}
          onChange={(e) => updateType(e.target.value)}
          disabled={linked}
        >
          {fabricTypes.map((type) => (
            <MenuItem key={type} value={type}>{type}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Collection"
          value={state.collection}
          onChange={(e) => updateCollection(e.target.value)}
          disabled={linked}
        >
          {collectionsForType.map((col) => (
            <MenuItem key={col} value={col}>{col}</MenuItem>
          ))}
        </TextField>

        <Box sx={{ opacity: linked ? 0.55 : 1, pointerEvents: linked ? 'none' : 'auto' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.8, display: 'block' }}>
            {swatchesForCollection.length} colors
            {currentSwatch && <> — <b>{currentSwatch.name}</b></>}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(44px, 1fr))',
              gap: 0.6,
              maxHeight: 180,
              overflowY: 'auto',
            }}
          >
            {swatchesForCollection.map((sw, idx) => (
              <FabricSwatch
                key={sw.url}
                url={sw.url}
                name={sw.name}
                selected={state.swatchIdx === idx}
                onClick={() => setState({ ...state, swatchIdx: idx })}
              />
            ))}
          </Box>
        </Box>
      </Stack>
    </Card>
  );
};

// ============================================================================
// PartMappingEditor — lets user assign each model material to a channel
// ============================================================================
const PartMappingEditor = ({ materialMap, mappings, setMapping, resetMappings }) => {
  if (!materialMap.length) return null;

  return (
    <Card sx={{ p: 2.5 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Part Mapping
          </Typography>
          <Tooltip title="Reset to auto-detected mapping">
            <Button
              size="small"
              startIcon={<RestoreIcon fontSize="small" />}
              onClick={resetMappings}
            >
              Reset
            </Button>
          </Tooltip>
        </Stack>

        <Typography variant="caption" color="text.secondary">
          Assign each part of the model to a material channel
        </Typography>

        <Stack spacing={1}>
          {materialMap.map((mat) => {
            const key = matKey(mat.name);
            const current = mappings[key] || 'ignore';
            return (
              <Stack key={mat.uuid} direction="row" spacing={1} alignItems="center">
                <Tooltip title={mat.name} arrow>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: 'monospace',
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {mat.name}
                  </Typography>
                </Tooltip>
                <TextField
                  select
                  size="small"
                  value={current}
                  onChange={(e) => setMapping(key, e.target.value)}
                  sx={{ minWidth: 140 }}
                >
                  {CHANNELS.map((ch) => (
                    <MenuItem key={ch.id} value={ch.id}>
                      {ch.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Card>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const Configurator = () => {
  // Per-channel selection state
  const [selectedMetal, setSelectedMetal] = useState(FINISH_LIBRARY[0]?.id || '');
  const [selectedWood, setSelectedWood] = useState('walnut');
  const [selectedCane, setSelectedCane] = useState('natural');

  // Default fabric state shared across all 3 fabric channels — each independent
  const defaultFabricState = () => {
    const firstType = Object.keys(FABRIC_LIBRARY)[0];
    const firstCollection = Object.keys(FABRIC_LIBRARY[firstType] || {})[0];
    return { type: firstType, collection: firstCollection, swatchIdx: 0 };
  };
  const [fabric1State, setFabric1State] = useState(defaultFabricState);
  const [fabric2State, setFabric2State] = useState(defaultFabricState);
  const [fabric3State, setFabric3State] = useState(defaultFabricState);

  // "Same as Fabric N" links. null = independent selection. Fabric 2 can mirror
  // Fabric 1; Fabric 3 can mirror Fabric 1 or Fabric 2. Each channel keeps its
  // own state above so unchecking restores the previous independent selection.
  const [fabric2LinkTo, setFabric2LinkTo] = useState(null); // null | 'fabric1'
  const [fabric3LinkTo, setFabric3LinkTo] = useState(null); // null | 'fabric1' | 'fabric2'

  // Mappings: { [matKey]: channelId }
  const [mappings, setMappings] = useState(() => loadStoredMappings());

  // Models
  const [models, setModels] = useState([]);
  const [activeModelIndex, setActiveModelIndex] = useState(0);
  const [loadError, setLoadError] = useState('');

  // Resolved presets
  const metalPreset = FINISH_LIBRARY.find((f) => f.id === selectedMetal) || FINISH_LIBRARY[0];
  const woodPreset = WOOD_PRESETS[selectedWood];
  const canePreset = CANE_PRESETS[selectedCane];

  const activeModel = models[activeModelIndex] || null;
  const loadedScene = activeModel?.scene || null;
  const materialMap = activeModel?.materialMap || [];
  const isLoading = models.some((m) => m.status === 'loading');
  const hasModels = models.length > 0;

  // Count how many parts are mapped to each channel
  const channelCounts = useMemo(() => {
    const counts = { metal: 0, wood: 0, cane: 0, fabric1: 0, fabric2: 0, fabric3: 0, ignore: 0 };
    materialMap.forEach((mat) => {
      const ch = mappings[matKey(mat.name)] || 'ignore';
      counts[ch] = (counts[ch] || 0) + 1;
    });
    return counts;
  }, [materialMap, mappings]);

  // Persist mappings whenever they change
  useEffect(() => {
    saveStoredMappings(mappings);
  }, [mappings]);

  // When a new model loads, seed mappings for any unseen materials using auto-classify
  useEffect(() => {
    if (!materialMap.length) return;
    setMappings((prev) => {
      let changed = false;
      const next = { ...prev };
      materialMap.forEach((mat) => {
        const k = matKey(mat.name);
        if (!(k in next)) {
          next[k] = mat.autoChannel;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [materialMap]);

  // --- Material builders ---
  const metalMaterial = useMemo(() => {
    const loader = new THREE.TextureLoader();
    const tex = loader.load(metalPreset.url);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(2, 2);
    tex.colorSpace = THREE.SRGBColorSpace;
    const mat = new THREE.MeshStandardMaterial({
      map: tex,
      color: metalPreset.tint || '#ffffff',
      metalness: metalPreset.metalness,
      roughness: metalPreset.roughness,
      envMapIntensity: 1.2,
      name: 'ConfiguratorMetal',
    });
    mat.userData._tex = tex;
    return mat;
  }, [metalPreset]);

  const woodMaterial = useMemo(() => {
    const tex = generateWoodTexture(woodPreset.color, 256);
    const mat = new THREE.MeshStandardMaterial({
      map: tex,
      color: 0xffffff,
      metalness: 0,
      roughness: woodPreset.roughness,
      envMapIntensity: 0.4,
      name: 'ConfiguratorWood',
    });
    mat.userData._tex = tex;
    return mat;
  }, [woodPreset]);

  const caneMaterial = useMemo(() => {
    const tex = generateCaneTexture(canePreset.color, 256);
    const mat = new THREE.MeshStandardMaterial({
      map: tex,
      color: 0xffffff,
      metalness: 0,
      roughness: 0.85,
      envMapIntensity: 0.5,
      name: 'ConfiguratorCane',
    });
    mat.userData._tex = tex;
    return mat;
  }, [canePreset]);

  const buildFabricMaterial = (state, name) => {
    const swatches = FABRIC_LIBRARY[state.type]?.[state.collection] || [];
    const swatch = swatches[state.swatchIdx] || swatches[0];
    if (!swatch) {
      return new THREE.MeshStandardMaterial({
        color: '#888',
        roughness: 0.8,
        name,
      });
    }
    const props = MATERIAL_PROPERTIES[state.type] || MATERIAL_PROPERTIES.Fabric;
    const loader = new THREE.TextureLoader();
    const tex = loader.load(swatch.url);
    // Swatches are photographic JPEGs, not seamless tiles. Plain RepeatWrapping
    // shows a hard seam at every tile boundary, which reads as a grid of boxes
    // on flat, camera-facing parts (e.g. the chair back). MirroredRepeatWrapping
    // flips each tile against its neighbor so edges line up and the seams vanish.
    tex.wrapS = THREE.MirroredRepeatWrapping;
    tex.wrapT = THREE.MirroredRepeatWrapping;
    tex.repeat.set(props.repeat, props.repeat);
    tex.colorSpace = THREE.SRGBColorSpace;
    const mat = new THREE.MeshStandardMaterial({
      map: tex,
      color: 0xffffff,
      metalness: props.metalness,
      roughness: props.roughness,
      envMapIntensity: props.envMapIntensity,
      name,
    });
    mat.userData._tex = tex;
    return mat;
  };

  // Resolve "Same as Fabric N" links to the state each channel actually uses.
  // Fabric 3 may point at Fabric 2, which may itself point at Fabric 1, so
  // resolve in order (1 → 2 → 3).
  const effFabric1State = fabric1State;
  const effFabric2State = fabric2LinkTo === 'fabric1' ? effFabric1State : fabric2State;
  const effFabric3State =
    fabric3LinkTo === 'fabric1'
      ? effFabric1State
      : fabric3LinkTo === 'fabric2'
      ? effFabric2State
      : fabric3State;

  const fabric1Material = useMemo(() => buildFabricMaterial(effFabric1State, 'ConfiguratorFabric1'), [effFabric1State]);
  const fabric2Material = useMemo(() => buildFabricMaterial(effFabric2State, 'ConfiguratorFabric2'), [effFabric2State]);
  const fabric3Material = useMemo(() => buildFabricMaterial(effFabric3State, 'ConfiguratorFabric3'), [effFabric3State]);

  // Bundle into one channels object passed to LoadedModel
  const channels = useMemo(
    () => ({
      metal: metalMaterial,
      wood: woodMaterial,
      cane: caneMaterial,
      fabric1: fabric1Material,
      fabric2: fabric2Material,
      fabric3: fabric3Material,
    }),
    [metalMaterial, woodMaterial, caneMaterial, fabric1Material, fabric2Material, fabric3Material]
  );

  // Dispose materials & textures when they change or unmount
  const disposeMat = (mat) => {
    mat?.userData?._tex?.dispose();
    mat?.dispose();
  };
  useEffect(() => () => disposeMat(metalMaterial), [metalMaterial]);
  useEffect(() => () => disposeMat(woodMaterial), [woodMaterial]);
  useEffect(() => () => disposeMat(caneMaterial), [caneMaterial]);
  useEffect(() => () => disposeMat(fabric1Material), [fabric1Material]);
  useEffect(() => () => disposeMat(fabric2Material), [fabric2Material]);
  useEffect(() => () => disposeMat(fabric3Material), [fabric3Material]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      models.forEach((m) => {
        if (m.url?.startsWith('blob:')) URL.revokeObjectURL(m.url);
      });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Model file handling ---
  const addFiles = useCallback((fileList) => {
    const files = Array.from(fileList).filter((f) => detectModelFormat(f.name));
    if (!files.length) {
      setLoadError('No supported files found. Use .fbx, .glb, .gltf, or .obj');
      return;
    }
    setLoadError('');
    files.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

    const newModels = files.map((file) => {
      const format = detectModelFormat(file.name);
      const url = URL.createObjectURL(file);
      return {
        name: file.name.replace(/\.[^.]+$/, ''),
        url,
        format,
        scene: null,
        materialMap: [],
        status: 'loading',
      };
    });

    setModels((prev) => [...prev, ...newModels]);

    newModels.forEach((model) => {
      loadModelFromUrl(model.url, model.format)
        .then(({ scene, materialMap: mMap }) => {
          setModels((current) =>
            current.map((m) =>
              m.url === model.url ? { ...m, scene, materialMap: mMap, status: 'ready' } : m
            )
          );
        })
        .catch((err) => {
          console.error(`Failed to load ${model.name}:`, err);
          setModels((current) =>
            current.map((m) => (m.url === model.url ? { ...m, status: 'error' } : m))
          );
        });
    });
  }, []);

  const removeModel = useCallback((index) => {
    setModels((prev) => {
      const model = prev[index];
      if (model?.url?.startsWith('blob:')) URL.revokeObjectURL(model.url);
      return prev.filter((_, i) => i !== index);
    });
    setActiveModelIndex((prev) => (prev >= index && prev > 0 ? prev - 1 : prev));
  }, []);

  const clearAll = useCallback(() => {
    models.forEach((m) => {
      if (m.url?.startsWith('blob:')) URL.revokeObjectURL(m.url);
    });
    setModels([]);
    setActiveModelIndex(0);
    setLoadError('');
  }, [models]);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
    },
    [addFiles]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // --- Mapping editing ---
  const setMapping = useCallback((key, channelId) => {
    setMappings((prev) => ({ ...prev, [key]: channelId }));
  }, []);

  const resetMappings = useCallback(() => {
    setMappings((prev) => {
      const next = { ...prev };
      materialMap.forEach((mat) => {
        next[matKey(mat.name)] = mat.autoChannel;
      });
      return next;
    });
  }, [materialMap]);

  // Active fabric summary for the bottom badge
  const fabricSummary = (state) => {
    const swatches = FABRIC_LIBRARY[state.type]?.[state.collection] || [];
    const sw = swatches[state.swatchIdx] || swatches[0];
    return sw ? `${state.type} · ${state.collection} · ${sw.name}` : '—';
  };

  return (
    <Box>
      <PageHeader
        title="3D Configurator"
        subtitle="Map each model part to a material channel, then configure each independently"
      />

      <Grid container spacing={2}>
        {/* Left panel */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={2}>
            {/* Models */}
            <Card sx={{ p: 2.5 }}>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Models {models.length > 0 && `(${models.length})`}
                  </Typography>
                  {models.length > 0 && (
                    <Button variant="text" color="error" size="small" onClick={clearAll}>
                      Clear All
                    </Button>
                  )}
                </Stack>

                <input
                  id="local-model-upload"
                  type="file"
                  accept=".fbx,.glb,.gltf,.obj"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    if (e.target.files?.length) addFiles(e.target.files);
                    e.target.value = '';
                  }}
                />

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => document.getElementById('local-model-upload')?.click()}
                >
                  {models.length === 0 ? 'Upload Models' : 'Add More Models'}
                </Button>

                {loadError && <Alert severity="error" sx={{ py: 0.5 }}>{loadError}</Alert>}

                {models.length > 1 && (
                  <>
                    <Divider />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Base Style
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {models.map((model, idx) => {
                        const baseMatch = model.name.match(/base\s*(\d+)/i);
                        const shortLabel = baseMatch ? `Base ${baseMatch[1]}` : `${idx + 1}`;
                        return (
                          <Chip
                            key={idx}
                            label={shortLabel}
                            size="small"
                            variant={activeModelIndex === idx ? 'filled' : 'outlined'}
                            color={activeModelIndex === idx ? 'primary' : 'default'}
                            onClick={() => setActiveModelIndex(idx)}
                            onDelete={models.length > 1 ? () => removeModel(idx) : undefined}
                            deleteIcon={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
                            sx={{ cursor: 'pointer' }}
                          />
                        );
                      })}
                    </Box>
                  </>
                )}

                {models.length === 1 && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      label={models[0].name}
                      color={models[0].status === 'ready' ? 'success' : models[0].status === 'error' ? 'error' : 'default'}
                      variant="outlined"
                      onDelete={() => removeModel(0)}
                      deleteIcon={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
                      sx={{ maxWidth: '100%' }}
                    />
                    {models[0].status === 'loading' && <CircularProgress size={14} />}
                  </Stack>
                )}
              </Stack>
            </Card>

            {/* Part Mapping Editor */}
            <PartMappingEditor
              materialMap={materialMap}
              mappings={mappings}
              setMapping={setMapping}
              resetMappings={resetMappings}
            />

            {/* Metal */}
            {(channelCounts.metal > 0 || !hasModels) && (
              <Card sx={{ p: 2.5 }}>
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Metal Finish
                    </Typography>
                    {channelCounts.metal > 0 && (
                      <Chip
                        size="small"
                        label={`${channelCounts.metal} part${channelCounts.metal > 1 ? 's' : ''}`}
                        color="info"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                    {FINISH_LIBRARY.map((preset) => (
                      <FabricSwatch
                        key={preset.id}
                        url={preset.url}
                        name={preset.label}
                        selected={selectedMetal === preset.id}
                        onClick={() => setSelectedMetal(preset.id)}
                      />
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary">{metalPreset.label}</Typography>
                </Stack>
              </Card>
            )}

            {/* Wood */}
            {channelCounts.wood > 0 && (
              <Card sx={{ p: 2.5 }}>
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Wood Finish
                    </Typography>
                    <Chip
                      size="small"
                      label={`${channelCounts.wood} part${channelCounts.wood > 1 ? 's' : ''}`}
                      color="info"
                      variant="outlined"
                    />
                  </Stack>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                    {Object.entries(WOOD_PRESETS).map(([key, preset]) => (
                      <ColorSwatch
                        key={key}
                        color={preset.color}
                        selected={selectedWood === key}
                        onClick={() => setSelectedWood(key)}
                        label={preset.label}
                      />
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary">{woodPreset.label}</Typography>
                </Stack>
              </Card>
            )}

            {/* Cane */}
            {channelCounts.cane > 0 && (
              <Card sx={{ p: 2.5 }}>
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Cane Finish
                    </Typography>
                    <Chip
                      size="small"
                      label={`${channelCounts.cane} part${channelCounts.cane > 1 ? 's' : ''}`}
                      color="info"
                      variant="outlined"
                    />
                  </Stack>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                    {Object.entries(CANE_PRESETS).map(([key, preset]) => (
                      <ColorSwatch
                        key={key}
                        color={preset.color}
                        selected={selectedCane === key}
                        onClick={() => setSelectedCane(key)}
                        label={preset.label}
                      />
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary">{canePreset.label}</Typography>
                </Stack>
              </Card>
            )}

            {/* Fabric 1, 2, 3 — each only shown if at least one part is assigned to that channel */}
            {channelCounts.fabric1 > 0 && (
              <FabricPickerCard
                channelId="fabric1"
                label="Fabric 1"
                partsCount={channelCounts.fabric1}
                state={fabric1State}
                setState={setFabric1State}
              />
            )}
            {channelCounts.fabric2 > 0 && (
              <FabricPickerCard
                channelId="fabric2"
                label="Fabric 2"
                partsCount={channelCounts.fabric2}
                state={effFabric2State}
                setState={setFabric2State}
                linkOptions={channelCounts.fabric1 > 0 ? [{ id: 'fabric1', label: 'Fabric 1' }] : []}
                linkedTo={fabric2LinkTo}
                setLinkedTo={setFabric2LinkTo}
              />
            )}
            {channelCounts.fabric3 > 0 && (
              <FabricPickerCard
                channelId="fabric3"
                label="Fabric 3"
                partsCount={channelCounts.fabric3}
                state={effFabric3State}
                setState={setFabric3State}
                linkOptions={[
                  ...(channelCounts.fabric1 > 0 ? [{ id: 'fabric1', label: 'Fabric 1' }] : []),
                  ...(channelCounts.fabric2 > 0 ? [{ id: 'fabric2', label: 'Fabric 2' }] : []),
                ]}
                linkedTo={fabric3LinkTo}
                setLinkedTo={setFabric3LinkTo}
              />
            )}

            {/* Hint when no fabric channels are in use yet */}
            {hasModels && channelCounts.fabric1 === 0 && channelCounts.fabric2 === 0 && channelCounts.fabric3 === 0 && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                Use the <b>Part Mapping</b> above to assign parts to Fabric 1, 2 or 3 to enable fabric pickers.
                You can use a different fabric for the seat, back, side, etc.
              </Alert>
            )}
          </Stack>
        </Grid>

        {/* 3D Viewer */}
        <Grid
          size={{ xs: 12, md: 8 }}
          sx={{
            // Keep the viewer pinned while the left column scrolls. alignSelf
            // flex-start stops the Grid item from stretching so sticky engages;
            // top clears the fixed TopHeader (~64px) with a small gap.
            position: { md: 'sticky' },
            top: { md: 80 },
            alignSelf: { md: 'flex-start' },
          }}
        >
          <Card
            sx={{
              p: 0,
              // Fill the right space down to the viewport bottom instead of a
              // fixed 700px, so the model stays large and fully visible.
              height: { xs: 480, md: 'calc(100vh - 96px)' },
              position: 'relative',
              border: !hasModels ? '2px dashed' : '2px solid transparent',
              borderColor: !hasModels ? 'divider' : 'transparent',
              overflow: 'hidden',
              borderRadius: 3,
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            {isLoading && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  position: 'absolute', top: 16, left: 20, zIndex: 10,
                  bgcolor: 'rgba(255,255,255,0.85)', px: 2, py: 0.8, borderRadius: 2,
                }}
              >
                <CircularProgress size={18} />
                <Typography variant="body2">Loading models...</Typography>
              </Stack>
            )}

            {!hasModels && (
              <Stack
                alignItems="center" justifyContent="center"
                sx={{ position: 'absolute', inset: 0, zIndex: 5, pointerEvents: 'none' }}
              >
                <Typography variant="h6" color="text.disabled" sx={{ mb: 0.5 }}>
                  Drop 3D models here
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  Supports .fbx, .glb, .gltf, .obj
                </Typography>
              </Stack>
            )}

            {activeModel?.status === 'ready' && (
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{
                  position: 'absolute', bottom: 16, left: 20, right: 20, zIndex: 10,
                  bgcolor: 'rgba(255,255,255,0.92)', px: 2, py: 0.8, borderRadius: 2,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {activeModel.name}
                </Typography>
                {channelCounts.metal > 0 && (
                  <>
                    <Typography variant="caption" color="text.disabled">|</Typography>
                    <Typography variant="caption">{metalPreset.label}</Typography>
                  </>
                )}
                {channelCounts.wood > 0 && (
                  <>
                    <Typography variant="caption" color="text.disabled">|</Typography>
                    <Typography variant="caption">{woodPreset.label}</Typography>
                  </>
                )}
                {channelCounts.cane > 0 && (
                  <>
                    <Typography variant="caption" color="text.disabled">|</Typography>
                    <Typography variant="caption">{canePreset.label} cane</Typography>
                  </>
                )}
                {channelCounts.fabric1 > 0 && (
                  <>
                    <Typography variant="caption" color="text.disabled">|</Typography>
                    <Typography variant="caption">F1: {fabricSummary(effFabric1State)}</Typography>
                  </>
                )}
                {channelCounts.fabric2 > 0 && (
                  <>
                    <Typography variant="caption" color="text.disabled">|</Typography>
                    <Typography variant="caption">F2: {fabricSummary(effFabric2State)}</Typography>
                  </>
                )}
                {channelCounts.fabric3 > 0 && (
                  <>
                    <Typography variant="caption" color="text.disabled">|</Typography>
                    <Typography variant="caption">F3: {fabricSummary(effFabric3State)}</Typography>
                  </>
                )}
              </Stack>
            )}

            <Canvas
              shadows
              flat
              camera={{ position: [3, 2.5, 4], fov: 42 }}
              gl={{ antialias: true }}
              style={{ background: '#f4f6fb' }}
            >
              {/* `flat` disables filmic tone-mapping + lights kept low so rendered
                  color matches the swatch thumbnail (no washing-out). */}
              <Environment preset="studio" environmentIntensity={0.45} />
              <ambientLight intensity={0.25} />
              <directionalLight
                intensity={0.65}
                position={[6, 8, 3]}
                castShadow
                shadow-mapSize-width={2048}
                shadow-mapSize-height={2048}
              />
              <directionalLight intensity={0.2} position={[-4, 3, -3]} />

              <Suspense fallback={null}>
                {loadedScene ? (
                  <LoadedModel
                    key={activeModel?.url}
                    scene={loadedScene}
                    mappings={mappings}
                    channels={channels}
                  />
                ) : (
                  <FallbackChair metalPreset={metalPreset} />
                )}
              </Suspense>

              <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -1.3, 0]} receiveShadow>
                <planeGeometry args={[20, 20]} />
                <meshStandardMaterial color="#ebedf4" />
              </mesh>
              <OrbitControls
                enableDamping
                minDistance={1}
                maxDistance={12}
                maxPolarAngle={Math.PI / 2 - 0.05}
                target={[0, 0, 0]}
              />
            </Canvas>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Configurator;
